import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useReorderSectionItemsMutation, useReorderSectionMutation } from '../redux/apiSlice';

// Styles - Embedded in component
const styles = `
  @keyframes slide-up {
    0% { transform: translateY(0); opacity: 1; }
    30% { transform: translateY(-8px); opacity: 0.8; }
    70% { transform: translateY(3px); opacity: 0.9; }
    100% { transform: translateY(0); opacity: 1; }
  }
  
  @keyframes slide-down {
    0% { transform: translateY(0); opacity: 1; }
    30% { transform: translateY(8px); opacity: 0.8; }
    70% { transform: translateY(-3px); opacity: 0.9; }
    100% { transform: translateY(0); opacity: 1; }
  }
  
  @keyframes slide-top {
    0% { transform: translateY(0); opacity: 1; }
    20% { transform: translateY(-20px); opacity: 0.7; }
    80% { transform: translateY(3px); opacity: 0.9; }
    100% { transform: translateY(0); opacity: 1; }
  }
  
  @keyframes slide-bottom {
    0% { transform: translateY(0); opacity: 1; }
    20% { transform: translateY(20px); opacity: 0.7; }
    80% { transform: translateY(-3px); opacity: 0.9; }
    100% { transform: translateY(0); opacity: 1; }
  }
  
  @keyframes fade-in {
    from { opacity: 0; transform: translateY(-10px); }
    to { opacity: 1; transform: translateY(0); }
  }
  
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }
  
  .animate-slide-up { animation: slide-up 0.5s ease; }
  .animate-slide-down { animation: slide-down 0.5s ease; }
  .animate-slide-top { animation: slide-top 0.6s ease; }
  .animate-slide-bottom { animation: slide-bottom 0.6s ease; }
  .animate-fade-in { animation: fade-in 0.3s ease; }
  .animate-pulse { animation: pulse 1.5s infinite; }
  
  .drag-handle {
    opacity: 0;
    transition: opacity 0.2s;
  }
  
  .group:hover .drag-handle {
    opacity: 1;
  }
  
  /* Responsive Design */
  @media (max-width: 640px) {
    .section-header {
      flex-direction: column;
      align-items: flex-start !important;
      gap: 0.75rem;
    }
    
    .section-controls {
      width: 100%;
      justify-content: space-between;
    }
    
    .stats-badges {
      flex-wrap: wrap;
    }
    
    .item-card {
      flex-direction: column;
    }
    
    .item-actions {
      width: 100%;
      justify-content: flex-end;
    }
    
    .questions-grid {
      grid-template-columns: 1fr !important;
    }
  }
  
  @media (max-width: 768px) {
    .stats-grid {
      grid-template-columns: repeat(2, 1fr) !important;
    }
    
    .metadata-grid {
      grid-template-columns: repeat(2, 1fr) !important;
    }
    
    .questions-grid {
      grid-template-columns: repeat(1, 1fr) !important;
    }
  }
  
  .questions-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 0.75rem;
  }
`;

// Sortable Item Component
const SortableItem = ({ id, children }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    cursor: isDragging ? 'grabbing' : 'grab',
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      {children}
    </div>
  );
};

const CurriculumTab = ({ course }) => {
  const navigate = useNavigate();
  const { courseId } = useParams();
  const [expandedSections, setExpandedSections] = useState([]);
  const [notification, setNotification] = useState(null);
  const [animatingSectionId, setAnimatingSectionId] = useState(null);
  const [animationDirection, setAnimationDirection] = useState('');
  
  const [reorderItems, { isLoading: isReorderingItems }] = useReorderSectionItemsMutation();
  const [reorderSection, { isLoading: isReorderingSection }] = useReorderSectionMutation();
  
  const [activeId, setActiveId] = useState(null);
  const [sectionItems, setSectionItems] = useState({});
  const [sections, setSections] = useState([]);

  // Initialize section items from course data
  useEffect(() => {
    if (!course?.sections) return;
    
    const itemsMap = {};
    const sortedSections = [...course.sections].sort((a, b) => (a.sequence || 0) - (b.sequence || 0));
    
    const processSection = (section) => {
      if (section.items && Array.isArray(section.items)) {
        itemsMap[section.id] = [...section.items].sort((a, b) => (a.order || 0) - (b.order || 0));
      } else {
        itemsMap[section.id] = [];
      }
      
      if (section.children && Array.isArray(section.children)) {
        section.children.forEach(child => processSection(child));
      }
    };
    
    sortedSections.forEach(section => processSection(section));
    setSectionItems(itemsMap);
    setSections(sortedSections);
  }, [course]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const toggleSection = (sectionId) => {
    setExpandedSections(prev =>
      prev.includes(sectionId) ? prev.filter(id => id !== sectionId) : [...prev, sectionId]
    );
  };

  const handleAddSection = () => {
    navigate(`/courses/create?edit=${courseId}&section=curriculum`);
  };

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const formatFileSize = (sizeInMB) => {
    if (!sizeInMB) return '0 MB';
    return `${sizeInMB.toFixed(1)} MB`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDuration = (seconds) => {
    if (!seconds || seconds === 0) return '0m';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
  };

  const getTotalDuration = () => {
    if (!course?.totalDurationHours) return '0h';
    const hours = course.totalDurationHours;
    return hours < 1 ? `${Math.round(hours * 60)}m` : `${hours.toFixed(1)}h`;
  };

  const calculateStatistics = () => {
    if (!course?.sections) return { 
      totalSections: 0, 
      totalVideos: 0, 
      totalPDFs: 0, 
      totalQuizzes: 0,
      totalItems: 0,
      totalChildSections: 0,
      totalQuizQuestions: 0
    };
    
    let totalVideos = 0;
    let totalPDFs = 0;
    let totalQuizzes = 0;
    let totalItems = 0;
    let totalChildSections = 0;
    let totalQuizQuestions = 0;
    
    const countSectionItems = (section) => {
      if (!section.items || !Array.isArray(section.items)) return;
      
      totalItems += section.items.length;
      
      section.items.forEach(item => {
        switch(item.type) {
          case 'VIDEO':
            totalVideos++;
            if (item.video?.pdfs && Array.isArray(item.video.pdfs)) {
              totalPDFs += item.video.pdfs.length;
            }
            if (item.video?.InstructorQuiz && Array.isArray(item.video.InstructorQuiz)) {
              totalQuizzes += item.video.InstructorQuiz.length;
              item.video.InstructorQuiz.forEach(quiz => {
                if (quiz.questions && Array.isArray(quiz.questions)) {
                  totalQuizQuestions += quiz.questions.length;
                }
              });
            }
            break;
          case 'PDF':
            if (item.pdf) totalPDFs++;
            break;
          case 'QUIZ':
            if (item.quiz) {
              totalQuizzes++;
              if (item.quiz.questions && Array.isArray(item.quiz.questions)) {
                totalQuizQuestions += item.quiz.questions.length;
              }
            }
            break;
          default:
            break;
        }
      });
      
      if (section.children && Array.isArray(section.children)) {
        totalChildSections += section.children.length;
        section.children.forEach(child => {
          countSectionItems(child);
        });
      }
    };
    
    course.sections.forEach(section => {
      countSectionItems(section);
    });
    
    return {
      totalSections: course.sections.length,
      totalVideos,
      totalPDFs,
      totalQuizzes,
      totalItems,
      totalChildSections,
      totalQuizQuestions
    };
  };

  const stats = calculateStatistics();

  const handleDragStart = useCallback((event) => {
    setActiveId(event.active.id);
  }, []);

  const handleDragEnd = useCallback(async (event) => {
    const { active, over } = event;
    
    if (active.id !== over.id) {
      const [sectionId, activeItemId] = active.id.split('-');
      const [, overItemId] = over.id.split('-');
      
      const draggedItemId = parseInt(activeItemId, 10);
      const targetItemId = parseInt(overItemId, 10);
      
      setSectionItems(prev => {
        const items = prev[sectionId] || [];
        const oldIndex = items.findIndex(item => 
          `${sectionId}-${item.id}-${item.type}` === active.id
        );
        const newIndex = items.findIndex(item => 
          `${sectionId}-${item.id}-${item.type}` === over.id
        );
        
        if (oldIndex === -1 || newIndex === -1) return prev;
        
        const newArray = arrayMove(items, oldIndex, newIndex);
        const updatedArray = newArray.map((item, index) => ({ ...item, order: index }));
        
        return { ...prev, [sectionId]: updatedArray };
      });

      try {
        await reorderItems({ draggedItemId, targetItemId }).unwrap();
        showNotification('Items reordered successfully', 'success');
      } catch (error) {
        showNotification(error.data?.message || 'Failed to reorder items', 'error');
      }
    }
    
    setActiveId(null);
  }, [reorderItems]);

  const handleDragCancel = useCallback(() => {
    setActiveId(null);
  }, []);

  const handleSectionReorder = async (sectionId, position) => {
    try {
      setAnimatingSectionId(sectionId);
      setAnimationDirection(position);
      
      const response = await reorderSection({ sectionId, position }).unwrap();
      
      if (response.success) {
        if (response.course && response.course.sections) {
          const updatedSections = [...response.course.sections].sort((a, b) => 
            (a.sequence || 0) - (b.sequence || 0)
          );
          setSections(updatedSections);
        } else {
          setSections(prevSections => {
            const updatedSections = [...prevSections];
            const currentIndex = updatedSections.findIndex(s => s.id === sectionId);
            
            if (currentIndex === -1) return prevSections;
            
            const [movedSection] = updatedSections.splice(currentIndex, 1);
            
            let newIndex;
            switch(position) {
              case 'top': newIndex = 0; break;
              case 'bottom': newIndex = updatedSections.length; break;
              case 'up': newIndex = Math.max(0, currentIndex - 1); break;
              case 'down': newIndex = Math.min(updatedSections.length, currentIndex + 1); break;
              default: newIndex = currentIndex;
            }
            
            updatedSections.splice(newIndex, 0, movedSection);
            return updatedSections.map((section, index) => ({ ...section, sequence: index }));
          });
        }
        
        showNotification(`Section moved ${position} successfully`, 'success');
      }
      
      setTimeout(() => {
        setAnimatingSectionId(null);
        setAnimationDirection('');
      }, 500);
      
    } catch (error) {
      showNotification(error.data?.message || `Failed to move section`, 'error');
      setAnimatingSectionId(null);
      setAnimationDirection('');
    }
  };

  const getAnimationClass = (sectionId) => {
    if (animatingSectionId !== sectionId) return '';
    return `animate-slide-${animationDirection}`;
  };

  const getActiveItem = () => {
    if (!activeId || !activeId.includes('-')) return null;
    const [sectionId, itemId, type] = activeId.split('-');
    const items = sectionItems[sectionId] || [];
    return items.find(item => item.id === parseInt(itemId) && item.type === type);
  };

  const activeItem = getActiveItem();

  // Render quiz questions
  const renderQuizQuestions = (quiz) => {
    if (!quiz.questions || quiz.questions.length === 0) return null;
    
    return (
      <div className="mt-3 border-t border-purple-100 pt-3">
        <p className="text-xs font-medium text-purple-700 mb-2 flex items-center gap-1">
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Questions ({quiz.questions.length})
        </p>
        <div className="questions-grid">
          {quiz.questions.map((question, qIdx) => (
            <div key={question.id || qIdx} className="bg-purple-50/50 rounded-lg p-2 border border-purple-100">
              <p className="text-xs font-medium text-gray-700 mb-1">
                Q{qIdx + 1}: {question.question}
              </p>
              <div className="space-y-1">
                {question.options && question.options.map((option, oIdx) => (
                  <div 
                    key={option.id || oIdx} 
                    className={`text-xs p-1.5 rounded ${
                      option.isCorrect 
                        ? 'bg-green-100 text-green-700 border border-green-200' 
                        : 'bg-white text-gray-600 border border-gray-100'
                    }`}
                  >
                    <div className="flex items-start gap-1">
                      <span className="font-medium">{String.fromCharCode(65 + oIdx)}.</span>
                      <span className="flex-1">{option.text}</span>
                      {option.isCorrect && (
                        <span className="text-green-600 font-bold">✓</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Render section items
  const renderSectionItems = (section) => {
    const items = sectionItems[section.id] || [];
    
    if (items.length === 0) {
      return (
        <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
          <svg className="w-10 h-10 text-gray-300 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <p className="text-sm text-gray-400">No items in this section</p>
        </div>
      );
    }

    return (
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <SortableContext
          items={items.map(item => `${section.id}-${item.id}-${item.type}`)}
          strategy={verticalListSortingStrategy}
        >
          <div className="space-y-3">
            {items.map((item) => {
              const itemId = `${section.id}-${item.id}-${item.type}`;
              
              if (item.type === 'VIDEO' && item.video) {
                const video = item.video;
                return (
                  <SortableItem key={itemId} id={itemId}>
                    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:border-red-300 hover:shadow-sm transition-all duration-200 group">
                      <div className="p-4">
                        <div className="flex flex-col sm:flex-row sm:items-start gap-3">
                          <div className="drag-handle hidden sm:block mt-1 text-gray-300 group-hover:text-gray-400">
                            ⋮⋮
                          </div>
                          
                          <div className="flex items-start gap-3 flex-1">
                            <div className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center flex-shrink-0">
                              <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                              </svg>
                            </div>
                            
                            <div className="flex-1 min-w-0">
                              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                                <h4 className="font-medium text-gray-900 truncate">{video.title}</h4>
                                <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded-full w-fit">
                                  Order #{item.order}
                                </span>
                              </div>
                              
                              {video.description && (
                                <p className="text-sm text-gray-500 mt-1 line-clamp-2">{video.description}</p>
                              )}
                              
                              <div className="flex flex-wrap items-center gap-2 mt-2">
                                <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded-full">
                                  ID: {video.id}
                                </span>
                                <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded-full">
                                  {formatDuration(video.duration)}
                                </span>
                                <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded-full">
                                  {formatDate(video.createdAt)}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {video.pdfs && video.pdfs.length > 0 && (
                        <div className="border-t border-gray-100 bg-blue-50/50 px-4 py-2">
                          <div className="flex items-center gap-1 text-xs text-blue-600 mb-1">
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                            </svg>
                            <span>{video.pdfs.length} Attached PDF{video.pdfs.length > 1 ? 's' : ''}</span>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {video.pdfs.map((pdf, idx) => (
                              <a
                                key={pdf.id || idx}
                                href={pdf.fileUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs px-2 py-1 bg-white border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors"
                              >
                                {pdf.fileName}
                              </a>
                            ))}
                          </div>
                        </div>
                      )}

                      {video.InstructorQuiz && video.InstructorQuiz.length > 0 && (
                        <div className="border-t border-gray-100">
                          {video.InstructorQuiz.map((quiz, quizIdx) => (
                            <div key={quiz.id || quizIdx} className="border-t border-gray-100 first:border-t-0">
                              <div className="bg-purple-50/30 px-4 py-2">
                                <div className="flex items-center gap-1 text-xs text-purple-600">
                                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                  </svg>
                                  <span className="font-medium">{quiz.title}</span>
                                  <span className="text-purple-400">•</span>
                                  <span>ID: {quiz.id}</span>
                                </div>
                              </div>
                              {renderQuizQuestions(quiz)}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </SortableItem>
                );
              }
              
              if (item.type === 'PDF' && item.pdf) {
                const pdf = item.pdf;
                return (
                  <SortableItem key={itemId} id={itemId}>
                    <div className="bg-white border border-blue-200 rounded-xl p-4 hover:border-blue-300 hover:shadow-sm transition-all duration-200 group">
                      <div className="flex flex-col sm:flex-row sm:items-start gap-3">
                        <div className="drag-handle hidden sm:block mt-1 text-gray-300 group-hover:text-gray-400">
                          ⋮⋮
                        </div>
                        
                        <div className="flex items-start gap-3 flex-1">
                          <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                            <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                            </svg>
                          </div>
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                              <h4 className="font-medium text-gray-900 truncate">{pdf.fileName}</h4>
                              <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded-full w-fit">
                                Order #{item.order}
                              </span>
                            </div>
                            
                            <div className="flex flex-wrap items-center gap-2 mt-2">
                              <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded-full">
                                ID: {pdf.id}
                              </span>
                              <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded-full">
                                {formatFileSize(pdf.fileSize)}
                              </span>
                              <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded-full">
                                {formatDate(pdf.createdAt)}
                              </span>
                            </div>
                          </div>
                          
                          <a 
                            href={pdf.fileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-3 py-1.5 bg-blue-500 text-white text-sm rounded-lg hover:bg-blue-600 transition-colors flex-shrink-0"
                          >
                            View
                          </a>
                        </div>
                      </div>
                    </div>
                  </SortableItem>
                );
              }
              
              if (item.type === 'QUIZ' && item.quiz) {
                const quiz = item.quiz;
                return (
                  <SortableItem key={itemId} id={itemId}>
                    <div className="bg-white border border-purple-200 rounded-xl overflow-hidden hover:border-purple-300 hover:shadow-sm transition-all duration-200 group">
                      <div className="p-4">
                        <div className="flex flex-col sm:flex-row sm:items-start gap-3">
                          <div className="drag-handle hidden sm:block mt-1 text-gray-300 group-hover:text-gray-400">
                            ⋮⋮
                          </div>
                          
                          <div className="flex items-start gap-3 flex-1">
                            <div className="w-8 h-8 rounded-lg bg-purple-50 flex items-center justify-center flex-shrink-0">
                              <svg className="w-4 h-4 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                              </svg>
                            </div>
                            
                            <div className="flex-1 min-w-0">
                              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                                <h4 className="font-medium text-gray-900 truncate">{quiz.title}</h4>
                                <div className="flex items-center gap-2">
                                  <span className={`text-xs px-2 py-1 rounded-full ${
                                    quiz.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                                  }`}>
                                    {quiz.isActive ? 'Active' : 'Inactive'}
                                  </span>
                                  <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded-full">
                                    #{item.order}
                                  </span>
                                </div>
                              </div>
                              
                              <div className="flex flex-wrap items-center gap-2 mt-2">
                                <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded-full">
                                  ID: {quiz.id}
                                </span>
                                <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded-full">
                                  {formatDate(quiz.createdAt)}
                                </span>
                                {quiz.questions && (
                                  <span className="text-xs px-2 py-1 bg-purple-100 text-purple-700 rounded-full">
                                    {quiz.questions.length} Questions
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      {renderQuizQuestions(quiz)}
                    </div>
                  </SortableItem>
                );
              }
              
              return null;
            })}
          </div>
        </SortableContext>
        
        <DragOverlay>
          {activeItem && (
            <div className="bg-white border-2 border-blue-500 shadow-lg rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className={`w-8 h-8 rounded-lg ${
                  activeItem.type === 'VIDEO' ? 'bg-red-50' :
                  activeItem.type === 'PDF' ? 'bg-blue-50' : 'bg-purple-50'
                } flex items-center justify-center`}>
                  <svg className={`w-4 h-4 ${
                    activeItem.type === 'VIDEO' ? 'text-red-500' :
                    activeItem.type === 'PDF' ? 'text-blue-500' : 'text-purple-500'
                  }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    {activeItem.type === 'VIDEO' && (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                    )}
                    {activeItem.type === 'PDF' && (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    )}
                    {activeItem.type === 'QUIZ' && (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    )}
                  </svg>
                </div>
                <span className="font-medium text-gray-900">
                  {activeItem.type === 'VIDEO' ? activeItem.video?.title :
                   activeItem.type === 'PDF' ? activeItem.pdf?.fileName :
                   activeItem.quiz?.title}
                </span>
              </div>
            </div>
          )}
        </DragOverlay>
      </DndContext>
    );
  };

  const renderSection = (section, level = 0) => {
    const isExpanded = expandedSections.includes(section.id);
    const hasChildren = section.children && section.children.length > 0;
    const animationClass = getAnimationClass(section.id);
    
    return (
      <div key={section.id} className={`mb-4 transition-all duration-500 ${animationClass}`}>
        <div className={`bg-white border border-gray-200 rounded-xl overflow-hidden ${level > 0 ? 'ml-4 md:ml-6' : ''}`}>
          <div 
            className="section-header px-4 md:px-6 py-4 bg-gray-50 flex items-center justify-between cursor-pointer hover:bg-gray-100/80 transition-colors"
            onClick={() => toggleSection(section.id)}
          >
            <div className="flex items-center gap-3 flex-1">
              <svg 
                className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`}
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
              
              <div className={`w-8 h-8 rounded-lg ${level === 0 ? 'bg-blue-50' : 'bg-green-50'} flex items-center justify-center flex-shrink-0`}>
                {level === 0 ? (
                  <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4-4m-4 4l4 4" />
                  </svg>
                )}
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="font-semibold text-gray-900">{section.name}</h3>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    level === 0 ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
                  }`}>
                    {level === 0 ? 'Parent' : 'Child'}
                  </span>
                  <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full">
                    #{section.sequence}
                  </span>
                </div>
                
                <div className="flex flex-wrap items-center gap-3 mt-1 text-xs text-gray-400">
                  <span>{formatDate(section.createdAt)}</span>
                  {section.parentId && <span>Parent ID: {section.parentId}</span>}
                </div>
              </div>
            </div>
            
            <div className="section-controls flex items-center gap-3">
              {level === 0 && (
                <div className="flex gap-1 mr-2" onClick={(e) => e.stopPropagation()}>
                  {['top', 'up', 'down', 'bottom'].map((pos) => (
                    <button
                      key={pos}
                      onClick={() => handleSectionReorder(section.id, pos)}
                      className="p-1.5 bg-white border border-gray-200 rounded-lg hover:bg-blue-50 hover:border-blue-200 transition-colors disabled:opacity-50"
                      title={`Move to ${pos}`}
                      disabled={isReorderingSection || animatingSectionId === section.id}
                    >
                      <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        {pos === 'top' && <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 11l7-7 7 7M5 19l7-7 7 7" />}
                        {pos === 'up' && <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />}
                        {pos === 'down' && <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />}
                        {pos === 'bottom' && <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 13l-7 7-7-7m14-8l-7 7-7-7" />}
                      </svg>
                    </button>
                  ))}
                </div>
              )}
              
              <div className="stats-badges flex gap-1">
                <span className="px-2 py-1 bg-red-50 text-red-700 text-xs rounded-full">
                  {sectionItems[section.id]?.filter(i => i.type === 'VIDEO').length || 0}
                </span>
                <span className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded-full">
                  {sectionItems[section.id]?.filter(i => i.type === 'PDF').length || 0}
                </span>
                <span className="px-2 py-1 bg-purple-50 text-purple-700 text-xs rounded-full">
                  {sectionItems[section.id]?.filter(i => i.type === 'QUIZ').length || 0}
                </span>
              </div>
            </div>
          </div>

          {isExpanded && (
            <div className="p-4 md:p-6 border-t border-gray-200">
              {renderSectionItems(section)}
              
              {hasChildren && (
                <div className="mt-6">
                  <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                    <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                    Child Sections ({section.children.length})
                  </h4>
                  <div className="space-y-3">
                    {section.children.map(child => renderSection(child, level + 1))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  if (!course) {
    return (
      <>
        <style>{styles}</style>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-500">Loading curriculum...</p>
          </div>
        </div>
      </>
    );
  }

  if (!course.sections || course.sections.length === 0) {
    return (
      <>
        <style>{styles}</style>
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Content Added Yet</h3>
          <p className="text-gray-500 mb-6">Start building your course curriculum</p>
          <button 
            onClick={handleAddSection}
            className="px-6 py-2.5 bg-blue-500 text-white rounded-lg font-medium hover:bg-blue-600 transition-colors"
          >
            Add First Section
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{styles}</style>
      
      {notification && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg animate-fade-in ${
          notification.type === 'success' ? 'bg-green-500' : 'bg-red-500'
        } text-white`}>
          <div className="flex items-center gap-2">
            <span>{notification.type === 'success' ? '✓' : '✕'}</span>
            <span className="text-sm">{notification.message}</span>
          </div>
        </div>
      )}

      {/* Header Stats */}
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-3">Course Content</h3>
        <div className="stats-grid grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-white border border-gray-200 rounded-lg p-3">
            <p className="text-xs text-gray-500 mb-1">Sections</p>
            <p className="text-xl font-semibold text-blue-600">{stats.totalSections}</p>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-3">
            <p className="text-xs text-gray-500 mb-1">Videos</p>
            <p className="text-xl font-semibold text-red-600">{stats.totalVideos}</p>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-3">
            <p className="text-xs text-gray-500 mb-1">PDFs</p>
            <p className="text-xl font-semibold text-blue-600">{stats.totalPDFs}</p>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-3">
            <p className="text-xs text-gray-500 mb-1">Quizzes</p>
            <p className="text-xl font-semibold text-purple-600">{stats.totalQuizzes}</p>
          </div>
        </div>
      </div>

      {/* Course Info */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
        <div className="metadata-grid grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <p className="text-xs text-gray-500">Course ID</p>
            <p className="text-sm font-medium text-gray-900">{course.id}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Status</p>
            <p className="text-sm">
              <span className={`px-2 py-1 rounded-full text-xs ${
                course.status === 'PUBLISHED' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
              }`}>
                {course.status || 'Draft'}
              </span>
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Language</p>
            <p className="text-sm font-medium text-gray-900">{course.language || 'N/A'}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Total Duration</p>
            <p className="text-sm font-medium text-gray-900">{getTotalDuration()}</p>
          </div>
        </div>
      </div>
      
      <div className="space-y-4">
        {sections.map(section => renderSection(section))}
      </div>

      {/* Instructions */}
      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <h5 className="text-sm font-medium text-blue-900 mb-2 flex items-center gap-2">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          How to use
        </h5>
        <ul className="text-xs text-blue-800 space-y-1">
          <li>• Drag items using the ⋮⋮ handle to reorder within section</li>
          <li>• Use the arrow buttons to reorder entire sections</li>
          <li>• Click section header to expand/collapse</li>
          <li>• Changes are auto-saved</li>
        </ul>
      </div>
    </>
  );
};

export default CurriculumTab;